import React, { useMemo } from "react";
import { iconsaxArrowDown } from "@wbly/tokens/icon";
import { Icon } from "../Icon";
import styles from "./SelectButton.module.css";

export type SelectButtonProps = {
  id?: string;
  variant?: "primary" | "secondary" | "tertiary";
  size?: "default" | "small" | "large";
  outline?: boolean;
  fullWidth?: boolean;
  value?: string;
  options?: { id: string; label: string }[];
  onChange?: (value: string) => void;
  className?: string;
};

function useStyleVariants({
  variant,
  size,
  outline,
  fullWidth,
  className,
}: SelectButtonProps & { className?: string }) {
  return useMemo(() => {
    const classNames = [styles.SelectButton];
    if (variant && variant !== "primary") {
      classNames.push(styles[`SelectButton-select--${variant}`]);
    }
    if (fullWidth) {
      classNames.push(styles["SelectButton-select--wide"]);
    }
    if (size && size !== "default") {
      classNames.push(styles[`SelectButton-select--${size}`]);
    }
    if (outline) {
      classNames.push(styles[`SelectButton-select--outline`]);
    }
    if (className) {
      classNames.push(className);
    }
    return classNames.join(" ");
  }, [variant, fullWidth, size, outline, className]);
}

export const SelectButton = React.forwardRef<
  HTMLSelectElement,
  SelectButtonProps
>(
  (
    {
      id,
      variant,
      size,
      outline = false,
      fullWidth = false,
      className,
      value,
      options = [],
      onChange,
      ...props
    }: SelectButtonProps,
    ref
  ) => {
    const classNames = useStyleVariants({
      variant,
      size,
      outline,
      fullWidth,
      className,
    });

    return (
      <div className={styles["SelectButton-wrapper"]}>
        <select
          id={id}
          className={`${styles["SelectButton-select"]} ${classNames}`}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          ref={ref}
          {...props}
        >
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
        <Icon href={iconsaxArrowDown} className={styles["SelectButton-icon"]} />
      </div>
    );
  }
);

SelectButton.displayName = "SelectButton";
